import { type FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { bookSelectOption } from '@/components/books/bookSelectOption'
import { MemberAvatar } from '@/components/clubs'
import { Button, Modal, Select } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import { useClubMembers } from '@/hooks/clubs'
import { useLibraryBooks } from '@/hooks/insights'
import { useMyLoans } from '@/hooks/loans'
import { useCreateTravelingBook } from '@/hooks/traveling'
import { cn } from '@/lib/utils'

/** Pick one of your books and the order of readers */
export function CreateTripModal({ clubId, onClose }: { clubId: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { data: books = [] } = useLibraryBooks()
  const { data: loans = [] } = useMyLoans()
  const { data: members = [] } = useClubMembers(clubId)
  const createTrip = useCreateTravelingBook()
  const [bookId, setBookId] = useState<string | null>(null)
  const [route, setRoute] = useState<string[]>([])
  const [note, setNote] = useState('')

  // Own copies that aren't lent right now
  const options = useMemo(() => {
    const lent = new Set(loans.filter((l) => !l.returned_on).map((l) => l.book_id))
    return books
      .filter((b) => b.ownership !== 'borrowed' && b.status !== 'wishlist' && !lent.has(b.id))
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((b) => bookSelectOption(b))
  }, [books, loans])

  const readers = members.filter((m) => m.user_id !== user?.id)
  const toggle = (userId: string) =>
    setRoute((r) => (r.includes(userId) ? r.filter((id) => id !== userId) : [...r, userId]))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!bookId || route.length === 0) return
    await createTrip.mutateAsync({ clubId, bookId, route, note })
    onClose()
  }

  return (
    <Modal isOpen onClose={onClose} title={t('traveling.create')}>
      <form onSubmit={submit} className="space-y-5 overflow-y-auto p-6">
        <div className="space-y-2">
          <label htmlFor="trip-book" className="text-sm font-medium text-foreground">
            {t('traveling.book')}
          </label>
          <Select
            id="trip-book"
            value={bookId}
            onChange={setBookId}
            options={options}
            placeholder={options.length ? t('traveling.chooseBook') : t('traveling.noBooks')}
            disabled={options.length === 0}
            searchable
            searchPlaceholder={t('search.searchByTitleAuthor')}
            emptyText={t('common.noResultsMessage')}
          />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{t('traveling.route')}</p>
          <p className="text-xs text-muted-foreground">{t('traveling.routeHint')}</p>
          <ul className="space-y-2">
            {readers.map((member) => {
              const order = route.indexOf(member.user_id)
              return (
                <li key={member.user_id}>
                  <button
                    type="button"
                    onClick={() => toggle(member.user_id)}
                    aria-pressed={order >= 0}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg border p-2 text-left transition',
                      order >= 0 ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                        order >= 0
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {order >= 0 ? order + 1 : ''}
                    </span>
                    <MemberAvatar
                      userId={member.user_id}
                      name={member.display_name}
                      avatarUrl={member.avatar_url}
                      size="sm"
                    />
                    <span className="truncate text-sm text-foreground">{member.display_name}</span>
                  </button>
                </li>
              )
            })}
          </ul>
          {readers.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('traveling.noReaders')}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="trip-note" className="text-sm font-medium text-foreground">
            {t('traveling.note')}{' '}
            <span className="font-normal text-muted-foreground">({t('common.optional')})</span>
          </label>
          <textarea
            id="trip-note"
            value={note}
            maxLength={1000}
            rows={2}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('traveling.notePlaceholder')}
            className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {createTrip.isError && <p className="text-sm text-error">{t('errors.general')}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!bookId || route.length === 0 || createTrip.isPending}>
            {createTrip.isPending ? t('common.saving') : t('traveling.start')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
